import { HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import assert from 'tiny-invariant';
import {
  catchError,
  filter,
  map,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';

import {
  ApiConsumerCallParams,
  ApiConsumerCallParamsWithBody,
  ApiConsumerCallParamsWithoutBody,
  ApiConsumerService,
} from '@ts/shared/api/data-access-api-consumer';
import { BormaDagoApiConsumerService } from '@ts/shared/api/data-access-borma-dago-api-consumer';
import {
  BormaDagoErrorCode,
  BormaDagoHttpErrorResponse,
} from '@ts/shared/api/util-borma-dago';
import {
  AuthJwtRenewalService,
  AuthJwtTokenStore,
} from '@ts/shared/authentication/data-access';

/**
 * Calls Borma Dago API with authentication information attached.
 *
 * If the user is not authenticated, the request will be dropped and not retried.
 */
@Injectable({
  providedIn: 'root',
})
export class BormaDagoAuthenticatedApiConsumerService implements ApiConsumerService {
  private bormaDagoApiConsumerService = inject(BormaDagoApiConsumerService);
  protected tokenStore = inject(AuthJwtTokenStore);
  protected renewalService = inject(AuthJwtRenewalService);

  protected getAuthenticationState$(): AuthJwtTokenStore['state$'] {
    return this.tokenStore.state$.pipe(take(1));
  }

  protected getCallParams$(
    params: ApiConsumerCallParams,
  ): Observable<ApiConsumerCallParams> {
    return this.getAuthenticationState$().pipe(
      // if user hasn't logged in, do nothing
      filter((state) => state !== null),
      take(1),
      map((state) => {
        // append Authorization: Bearer to headers.
        const existingHeaders =
          params.httpOptions?.headers || new HttpHeaders();

        assert(state !== null);

        return {
          ...params,
          httpOptions: {
            ...params.httpOptions,
            headers: existingHeaders.append(
              'Authorization',
              `Bearer ${state.access}`,
            ),
          },
        };
      }),
    );
  }

  /**
   * The error code that means the credential attached by
   * {@link getCallParams$} is no longer accepted.
   */
  protected getTokenNotValidErrorCode(): BormaDagoErrorCode {
    return this.tokenStore.TOKEN_NOT_VALID_ERROR_CODE;
  }

  /**
   * Renews the credential attached by {@link getCallParams$} after the server
   * refused it; the call is retried once when this emits.
   */
  protected renewAfterRejection$(): Observable<unknown> {
    return this.renewalService.renew$();
  }

  private call$<T>(baseParams: ApiConsumerCallParams): Observable<T> {
    const tokenNotValidErrorCode = this.getTokenNotValidErrorCode();
    // Fetch the authentication state
    return this.getCallParams$(baseParams).pipe(
      // Use it to try and call the API, and handle the case when the token is expired
      switchMap((params) => {
        return this.bormaDagoApiConsumerService.call$<T>({
          ...params,
          errorCodesToHandleManually: (
            params.errorCodesToHandleManually || []
          ).concat(tokenNotValidErrorCode),
        });
      }),
      // On failure, if it's due to token expired:
      // renew token until it succeed and then try ONE more time
      catchError((error: BormaDagoHttpErrorResponse) => {
        if (error.error.error_code === tokenNotValidErrorCode) {
          // refresh token, then get the new params, then try to call the api again
          return this.renewAfterRejection$().pipe(
            switchMap(() => this.getCallParams$(baseParams)),
            switchMap((params) =>
              this.bormaDagoApiConsumerService.call$<T>(params),
            ),
          );
        } else {
          return throwError(() => error);
        }
      }),
    );
  }

  get$<T>(params: ApiConsumerCallParamsWithoutBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'get' });
  }
  delete$<T>(params: ApiConsumerCallParamsWithoutBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'delete' });
  }
  post$<T>(params: ApiConsumerCallParamsWithBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'post' });
  }
  put$<T>(params: ApiConsumerCallParamsWithBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'put' });
  }
  patch$<T>(params: ApiConsumerCallParamsWithBody): Observable<T> {
    return this.call$<T>({ ...params, method: 'patch' });
  }
}
